import type { Point } from '../objects/StrokeEvaluator';

export const DISTANCE_SAMPLE_SPACING = 8;
const PROJECTION_EPSILON = 1e-7;
const BRANCH_OFFSET_TOLERANCE = 0.5;

type PathSample = { point: Point; distance: number };
export type PathProjection = PathSample & { segmentIndex: number; offset: number };

export class StrokePath {
	constructor(private readonly samples: readonly PathSample[]) {}

	segmentAt(distance: number) {
		const index = this.samples.findIndex((sample) => sample.distance >= distance);
		return Math.max(0, Math.min(this.samples.length - 2, index < 0 ? this.samples.length - 2 : index - 1));
	}

	project(point: Point, segmentIndex: number, searchDistance = 0, movement?: Point): PathProjection {
		let current = this.projectSegment(point, segmentIndex);
		if (searchDistance > 0) {
			const origin = current.distance;
			const neighborhood = searchDistance + 2 * current.offset;
			// Search through a local minimum at a rounded turn without reaching unrelated path sections.
			for (const direction of [-1, 1]) {
				for (
					let index = segmentIndex + direction;
					index >= 0 && index < this.samples.length - 1;
					index += direction
				) {
					const candidate = this.projectSegment(point, index);
					if (Math.abs(candidate.distance - origin) > neighborhood) break;
					if (this.isBetterProjection(candidate, current, origin, movement)) current = candidate;
				}
			}
			return current;
		}
		while (true) {
			let nearest = current;
			for (const index of [current.segmentIndex - 1, current.segmentIndex + 1]) {
				if (index < 0 || index >= this.samples.length - 1) continue;
				const candidate = this.projectSegment(point, index);
				if (candidate.offset < nearest.offset - PROJECTION_EPSILON) nearest = candidate;
			}
			if (nearest === current) return current;
			current = nearest;
		}
	}

	private isBetterProjection(candidate: PathProjection, current: PathProjection, origin: number, movement?: Point) {
		if (movement) {
			const candidateAlignment = this.getAlignment(candidate.segmentIndex, movement);
			if (candidate.distance > origin && candidateAlignment <= 0) return false;
			// Overlapping turn branches differ slightly after sampling; use direction to break that tie.
			if (Math.abs(candidate.offset - current.offset) < BRANCH_OFFSET_TOLERANCE) {
				const candidateForward = candidateAlignment > 0;
				const currentForward = this.getAlignment(current.segmentIndex, movement) > 0;
				if (candidateForward !== currentForward) return candidateForward;
			}
		}
		return candidate.offset < current.offset - PROJECTION_EPSILON;
	}

	private getAlignment(segmentIndex: number, movement: Point) {
		const start = this.samples[segmentIndex].point;
		const end = this.samples[segmentIndex + 1].point;
		return (end.x - start.x) * movement.x + (end.y - start.y) * movement.y;
	}

	private projectSegment(point: Point, segmentIndex: number): PathProjection {
		const start = this.samples[segmentIndex];
		const end = this.samples[segmentIndex + 1] ?? start;
		const dx = end.point.x - start.point.x;
		const dy = end.point.y - start.point.y;
		const lengthSquared = dx * dx + dy * dy;
		const progress =
			lengthSquared === 0
				? 0
				: Math.max(
						0,
						Math.min(1, ((point.x - start.point.x) * dx + (point.y - start.point.y) * dy) / lengthSquared),
				  );
		const projected = { x: start.point.x + dx * progress, y: start.point.y + dy * progress };
		return {
			segmentIndex,
			point: projected,
			distance: start.distance + (end.distance - start.distance) * progress,
			offset: Math.hypot(point.x - projected.x, point.y - projected.y),
		};
	}
}
